import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import {
  FaBars,
  FaTimes,
  FaSearch,
  FaHeart,
  FaBookmark,
  FaUser,
  FaSignOutAlt,
  FaSignInAlt,
  FaUserPlus,
  FaHome,
  FaSun,
  FaMoon
} from "react-icons/fa";

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme, isDark } = useTheme();
  const token = localStorage.getItem("token");
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
    setMenuOpen(false);
  };

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: "/", label: "Discover", icon: FaHome },
    ...(token ? [
      { path: "/favorites", label: "Favorites", icon: FaHeart },
      { path: "/watchlist", label: "Watchlist", icon: FaBookmark },
      { path: "/profile", label: "Profile", icon: FaUser }
    ] : [
      { path: "/login", label: "Login", icon: FaSignInAlt },
      { path: "/register", label: "Register", icon: FaUserPlus }
    ])
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
      ? `theme-bg-primary/95 backdrop-blur-md theme-shadow-lg theme-border border-b`
      : isDark
        ? 'bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900'
        : 'bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700'
      }`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-2 group"
          >
            <div className="text-xl sm:text-2xl group-hover:scale-110 transition-transform duration-200">
              🎬
            </div>
            <span className={`font-bold text-lg sm:text-xl transition-colors duration-300 ${isScrolled ? 'text-gray-900' : 'text-white'
              }`}>
              MovieMate
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${isActive(path)
                  ? isScrolled
                    ? 'bg-purple-100 text-purple-700 font-semibold'
                    : 'bg-white/20 text-white font-semibold'
                  : isScrolled
                    ? 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
              >
                <Icon className="text-sm" />
                <span>{label}</span>
              </Link>
            ))}

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${isScrolled
                ? 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
            >
              {isDark ? <FaSun className="text-lg" /> : <FaMoon className="text-lg" />}
            </button>

            {token && (
              <button
                onClick={handleLogout}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${isScrolled
                  ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
                  : 'text-red-300 hover:bg-red-500/20 hover:text-red-200'
                  }`}
              >
                <FaSignOutAlt className="text-sm" />
                <span>Logout</span>
              </button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={toggleMenu}
              className={`p-2 rounded-lg transition-colors duration-200 ${isScrolled
                ? 'text-gray-700 hover:bg-gray-100'
                : 'text-white hover:bg-white/10'
                }`}
            >
              {menuOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}>
          <div className={`py-4 space-y-2 ${isScrolled ? 'bg-white border-t border-gray-200' : 'bg-slate-800/95 backdrop-blur-md'
            }`}>
            {navItems.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${isActive(path)
                  ? isScrolled
                    ? 'bg-purple-100 text-purple-700 font-semibold'
                    : 'bg-white/20 text-white font-semibold'
                  : isScrolled
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-white/80 hover:bg-white/10'
                  }`}
              >
                <Icon className="text-lg" />
                <span>{label}</span>
              </Link>
            ))}

            {/* Mobile Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 w-full text-left ${isScrolled
                ? 'text-gray-700 hover:bg-gray-100'
                : 'text-white/80 hover:bg-white/10'
                }`}
            >
              {isDark ? <FaSun className="text-lg" /> : <FaMoon className="text-lg" />}
              <span>Switch to {isDark ? 'Light' : 'Dark'} Mode</span>
            </button>

            {token && (
              <button
                onClick={handleLogout}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 w-full text-left ${isScrolled
                  ? 'text-red-600 hover:bg-red-50'
                  : 'text-red-300 hover:bg-red-500/20'
                  }`}
              >
                <FaSignOutAlt className="text-lg" />
                <span>Logout</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;